import { useEffect, useRef } from 'react';
import { useTheme } from 'context/ThemeContext.jsx';
import { Button } from 'components/common';

/**
 * The first-run interface guide — "J.A.R.V.I.S. CONSOLE".
 *
 * Shown once, after the boot sequence has finished, to explain that there are
 * three interfaces over one dataset. The "?" button in the V3 ticker reopens it
 * on demand, which is why `open` is a prop rather than being derived from
 * `onboardingSeen` alone.
 *
 * The original was a static block with every style inline and no way to close it
 * except the button — no Escape, no backdrop click, and no focus management, so
 * a keyboard user landed behind it. All three are handled here.
 */
const INTERFACES = [
  {
    tag: 'V1',
    tone: 'v1',
    name: 'Classic Dashboard',
    blurb: 'The original CRM — bright, familiar, indigo accents. No effects.',
  },
  {
    tag: 'V2',
    tone: 'v2',
    name: 'Console',
    blurb: 'Hacker terminal — cyan/green palette, matrix rain, HUD panels, boot sequence.',
  },
  {
    tag: 'V3',
    tone: 'v3',
    name: 'Jarvis — Command Center',
    blurb: 'Mission control — left nav rail, multi-ring radial gauges, live ticker, week projections.',
  },
];

export default function OnboardingModal({ open, onClose }) {
  const { theme } = useTheme();
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="onboard"
      role="presentation"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="onboard__panel" role="dialog" aria-modal="true" aria-labelledby="onboard-title">
        <div className="onboard__accent" />

        <div className="onboard__eyebrow">SYSTEM UPGRADE</div>
        <h2 className="onboard__title" id="onboard-title">J.A.R.V.I.S. CONSOLE</h2>

        <div className="onboard__section">
          <div className="onboard__label">THREE INTERFACES · ONE DATASET</div>
          <div className="onboard__list">
            {INTERFACES.map(i => (
              <div className={`onboard__row onboard__row--${i.tone}`} key={i.tag}>
                <div className={`onboard__tag onboard__tag--${i.tone}`}>{i.tag}</div>
                <div>
                  <div className="onboard__name">
                    {i.name}
                    {theme === i.tone && <span className="onboard__here"> ← YOU ARE HERE</span>}
                  </div>
                  <div className="onboard__blurb">{i.blurb}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="onboard__controls">
          <div className="onboard__label onboard__label--dim">CONTROLS</div>
          <div className="onboard__control-list">
            <div><span className="onboard__key">THEME button</span> — cycles V1 → V2 → V3 live. No reload needed.</div>
            <div><span className="onboard__key">BOOT button</span> — toggles the cinematic intro on/off.</div>
            <div><span className="onboard__key">? button</span> — reopens this guide anytime.</div>
            <div className="onboard__note">Your preferences are saved automatically.</div>
          </div>
        </div>

        <Button variant="cta" onClick={onClose} ref={closeRef}>
          Enter console
        </Button>
      </div>
    </div>
  );
}
